// Browser-friendly Final Draft (.fdx) parser.
//
// Port of src/main/parsers/fdx.js, swapping xml2js (CommonJS, node-only
// in this codebase) for fast-xml-parser (already in deps, ESM-friendly).
// Algorithm preserved 1:1 — DualDialogue expansion, title-page
// extraction, scene/character classification all behave the same.

import { XMLParser } from 'fast-xml-parser';
import {
  FDX_TYPE_MAP,
  newScene,
  newBlock,
  recordCharacter,
  recordDialogue,
  characterKey,
} from './shared.js';

// Configure to mirror xml2js's preferred shape: attributes grouped
// under `$`, text content under `_`. Lets the rest of the parser stay
// nearly identical to the Electron version.
const PARSER = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '',
  attributesGroupName: '$',
  textNodeName: '_',
  parseAttributeValue: false,
  trimValues: false,
});

function extractText(node) {
  if (node == null) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node._) return String(node._);
  if (node.Text) return extractText(node.Text);
  if (typeof node === 'object') {
    return Object.keys(node)
      .filter((k) => k !== '$')
      .map((k) => extractText(node[k]))
      .join('');
  }
  return '';
}

function extractTitlePage(fd) {
  let title = 'Untitled';
  const titlePage = {};
  const root = fd && fd.TitlePage && fd.TitlePage.Content;
  if (!root || !root.Paragraph) return { title, titlePage };

  const tpParas = Array.isArray(root.Paragraph) ? root.Paragraph : [root.Paragraph];
  const texts = tpParas.map((p) => extractText(p).trim()).filter((t) => t.length > 0);
  if (texts.length === 0) return { title, titlePage };

  title = texts[0];
  titlePage.title = texts[0];

  if (texts.length > 1) {
    const second = texts[1];
    if (/^(written|story|screenplay|teleplay|adaptation|by\b)/i.test(second)) {
      titlePage.credit = second;
      if (texts.length > 2) titlePage.author = texts[2];
      if (texts.length > 3) titlePage.source = texts[3];
    } else {
      titlePage.author = second;
      if (texts.length > 2) titlePage.source = texts[2];
    }
  }
  return { title, titlePage };
}

function expandDualDialogue(paragraphs) {
  return paragraphs.flatMap((p) => {
    if (!p || typeof p !== 'object') return [p];
    const dd = p.DualDialogue;
    if (!dd) return [p];
    const ddArr = Array.isArray(dd) ? dd : [dd];
    const inner = [];
    for (const cluster of ddArr) {
      let kids = cluster && cluster.Paragraph;
      if (!kids) continue;
      if (!Array.isArray(kids)) kids = [kids];
      for (const k of kids) inner.push(k);
    }
    const wrapperText = p.Text != null ? extractText(p.Text).trim() : '';
    if (wrapperText) {
      inner.unshift({ $: { Type: (p.$ && p.$.Type) || 'Character' }, Text: wrapperText });
    }
    return inner.length > 0 ? inner : [p];
  });
}

export async function parseFDX(rawText) {
  // Strip UTF-8 BOM Final Draft writes at the start of .fdx files.
  let xmlText = rawText;
  if (typeof xmlText === 'string' && xmlText.charCodeAt(0) === 0xfeff) {
    xmlText = xmlText.slice(1);
  }
  let xml;
  try {
    xml = PARSER.parse(xmlText);
  } catch (err) {
    throw new Error(`FDX parse failed: ${err?.message || err}`);
  }
  const fd = xml && (xml.FinalDraft || xml.finaldraft);
  if (!fd) throw new Error('Not a valid Final Draft XML file');

  const { title, titlePage } = extractTitlePage(fd);

  const content = fd.Content || {};
  let paragraphs = content.Paragraph || [];
  if (!Array.isArray(paragraphs)) paragraphs = [paragraphs];
  paragraphs = expandDualDialogue(paragraphs);

  const scenes = [];
  const prelude = [];
  const characters = {};
  let currentScene = null;
  let lastCharacter = null;
  let sceneNumber = 0;

  for (const para of paragraphs) {
    const attrs = para && para.$ ? para.$ : {};
    const rawType = attrs.Type || 'Action';
    const type = FDX_TYPE_MAP[rawType] || 'action';
    const text = extractText(para).trim();
    if (!text) continue;

    if (type === 'scene-heading') {
      sceneNumber += 1;
      currentScene = newScene(sceneNumber, text);
      scenes.push(currentScene);
      lastCharacter = null;
      continue;
    }

    if (!currentScene) {
      prelude.push(newBlock(type, text));
      if (type === 'character') {
        lastCharacter = characterKey(text);
        recordCharacter(characters, lastCharacter, null);
      } else if (type === 'dialogue' && lastCharacter) {
        recordDialogue(characters, lastCharacter, text);
      } else if (type !== 'parenthetical') {
        lastCharacter = null;
      }
      continue;
    }

    currentScene.blocks.push(newBlock(type, text));

    if (type === 'character') {
      lastCharacter = characterKey(text);
      recordCharacter(characters, lastCharacter, currentScene);
    } else if (type === 'dialogue' && lastCharacter) {
      recordDialogue(characters, lastCharacter, text);
    } else if (type !== 'parenthetical') {
      lastCharacter = null;
    }
  }

  return { title, titlePage, prelude, scenes, characters };
}
